import * as React from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CheckCircleIcon,
  ReceiptIcon,
  ReportIssueIcon,
  Text,
  TextVariant,
  WarningIcon,
} from "../../ui";
import { PartOrder, PartOrderItemV2, PartOrderStatus } from "../../../generated/nest-graphql";
import { ItemStatusChip, OrderStatusChip } from "../PartStatusChip";
import {
  formatJobWindow,
  formatPickedUpAt,
  isAwaitingPickup,
  jobWindowOf,
  PartsFilter,
  summarisePickup,
} from "../partsStatus.utils";

/**
 * One part order. Items are listed in full rather than filtered to the active tab —
 * at the counter, what you've already collected is the context for what's left.
 */
export const PartOrderCard: React.FC<{
  order: PartOrder;
  filter: PartsFilter;
  onConfirmPickup: (order: PartOrder) => void;
  onReportIssue: (order: PartOrder) => void;
}> = ({ order, onConfirmPickup, onReportIssue }) => {
  const history = useHistory();
  const summary = summarisePickup(order);
  const items = (order?.items ?? []).filter(Boolean) as PartOrderItemV2[];
  const canceled = order?.status === PartOrderStatus.Canceled;
  const withdrawn = !!order?.job?.isWithdrawn;
  // Nothing more should be collected for a withdrawn job; what was collected goes back.
  const anythingToCollect = !withdrawn && items.some(isAwaitingPickup);
  // A partly-collected order is not being confirmed, it is being finished.
  const pickupLabel = summary.state === "PARTIAL" ? "Pick Up Remaining Items" : "Confirm Pickup";
  const vehicle = order?.job?.vehicleInfo;
  const jobId = order?.job?.id;
  const vehicleLabel = vehicle?.year ? `${vehicle.year} ${vehicle.make ?? ""} ${vehicle.model ?? ""}`.trim() : "";
  const { start: windowStart, end: windowEnd } = jobWindowOf(order);
  const scheduleLabel = formatJobWindow(windowStart, windowEnd);

  return (
    <div className="border border-[#55697f] rounded-md bg-[#2b3a4f] overflow-hidden">
      <div className="p-3 flex flex-col gap-1 border-b border-[#44576e]">
        <div className="flex items-start justify-between gap-2">
          <span className="flex items-center gap-2 min-w-0">
            <ReceiptIcon style={{ fontSize: 18, color: "#94a3b8", flexShrink: 0 }} />
            <Text variant={TextVariant.H6} style={{ fontSize: 17, fontWeight: 600 }}>
              {order?.orderNumber || "No order number"}
            </Text>
          </span>
          <OrderStatusChip summary={summary} />
        </div>
        {/* The whole job block is the target, not just the number — it is one idea, and
            a number-sized tap target is unusable with gloves on. */}
        <button
          type="button"
          onClick={() => jobId && history.push(`/jobs/${jobId}`)}
          disabled={!jobId}
          aria-label={jobId ? `Open job ${order?.job?.jobNumber ?? ""}` : undefined}
          className="w-full text-left bg-transparent border-0 p-0 m-0 cursor-pointer disabled:cursor-default"
        >
          <Text variant={TextVariant.BODY2} style={{ color: jobId ? "#cbd5e1" : "#94a3b8" }}>
            Job# <span style={{ textDecoration: jobId ? "underline" : "none" }}>{order?.job?.jobNumber ?? "—"}</span>
            {vehicleLabel ? ` | ${vehicleLabel}` : ""}
          </Text>
          {scheduleLabel && (
            <Text variant={TextVariant.BODY2} className="text-gray-400">
              {scheduleLabel}
            </Text>
          )}
        </button>
        {withdrawn && (
          <div className="flex items-center gap-1 mt-1">
            <WarningIcon style={{ fontSize: 16, color: "#f0b429" }} />
            <Text variant={TextVariant.BODY2} style={{ color: "#f0b429" }}>
              Job withdrawn: return these parts
            </Text>
          </div>
        )}
        {order?.partOrderingIssue && (
          <div className="flex items-center gap-1 mt-1">
            <WarningIcon style={{ fontSize: 16, color: "#f0b429" }} />
            <Text variant={TextVariant.BODY2} style={{ color: "#f0b429" }}>
              Reported: {order.partOrderingIssue}
            </Text>
          </div>
        )}
      </div>

      <div className={`bg-[#1f2b39] ${!canceled ? "border-b border-[#44576e]" : ""}`}>
        {items.length > 0 ? (
          <ul className="divide-y divide-[#3a4b60] list-none p-0 m-0">
            {items.map((item) => {
              // The chip beside this row already says "Picked up", so the time stands alone.
              const pickedUpAt = formatPickedUpAt(item);
              return (
                <li key={item.id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold truncate">{item.name || "Unnamed part"}</span>
                    <span className="block text-sm text-gray-400">
                      {item.units ?? 1}&times; {item.partNumber || "no part number"}
                      {pickedUpAt ? ` · ${pickedUpAt}` : ""}
                    </span>
                  </span>
                  <ItemStatusChip item={item} />
                </li>
              );
            })}
          </ul>
        ) : (
          <Text variant={TextVariant.BODY2} className="text-gray-400 px-3 py-3">
            No parts listed on this order yet.
          </Text>
        )}
      </div>

      {!canceled && (
        <div className="flex gap-2 p-3">
          {anythingToCollect && (
            <Button
              size={ButtonSize.SMALL}
              variant={ButtonVariant.CONTAINED}
              flat
              fullWidth
              startIcon={<CheckCircleIcon style={{ fontSize: "1rem" }} />}
              onClick={() => onConfirmPickup(order)}
              style={{ textTransform: "none", fontWeight: 600, backgroundColor: "#a7f3d0", color: "#065f46" }}
            >
              {pickupLabel}
            </Button>
          )}
          <Button
            size={ButtonSize.SMALL}
            variant={ButtonVariant.CONTAINED}
            flat
            fullWidth
            startIcon={<ReportIssueIcon style={{ fontSize: "1rem" }} />}
            onClick={() => onReportIssue(order)}
            style={{ textTransform: "none", fontWeight: 600, backgroundColor: "#fecaca", color: "#7f1d1d" }}
          >
            Report Problem
          </Button>
        </div>
      )}
    </div>
  );
};
